import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  isSuperUser,
  isCustomerCare,
  userIsAllowedTo,
  carrierIsAllowedTo,
  carrierIsIboxen,
  seeingAsAnotherCarrier,
} from '../utils/permissions'

export const useMe = () => {
  const { user, carrier, carrierRolePermissions } = useSelector(
    (state) => state.root
  )

  return useMemo(() => {
    // Picking another carrier in the header sends every request as them, and the server answers
    // with their carrier, so `carrier` here is already theirs. What did not change was this user:
    // a super user is allowed everything, so the page kept showing iBoxen's own buttons over
    // someone else's data.
    //
    // While acting as another carrier, that blanket allowance is dropped and the carrier's own
    // permissions answer for both halves — the point of switching is to see what they see. A
    // super user's own carrier is iBoxen, so being scoped to any other carrier is what acting as
    // one means.
    //
    // This hides things; it cannot reveal any. The server authorises from the real token and is
    // untouched by this, so a button that is gone here was never the thing standing in the way.
    // What this carrier's own people may do comes from their roles, which the server sends as
    // carrierRolePermissions. Not from the carrier's own `allowedTo`: that is a different
    // vocabulary — it says `usePickups` where the screens ask for `createParcels` — so using it
    // here answered "no" to every question the menu asks and emptied it.
    const seeingAsAnother = seeingAsAnotherCarrier(user, carrier)
    const theirs = carrierRolePermissions
    const allowed = (action) =>
      seeingAsAnother && theirs
        ? theirs.includes(action)
        : userIsAllowedTo(user, action)

    return {
      user: {
        ...user,
        allowedTo: user?.role?.allowedTo,
        isAllowedTo: allowed,
        isSuperUser: seeingAsAnother ? false : isSuperUser(user),
        isCustomerCare: isCustomerCare(user),
        // what they actually are, for the few places that need to know they could switch back
        isReallySuperUser: isSuperUser(user),
        seeingAsAnother,
      },
      carrier: {
        ...carrier,
        isAllowedTo: (action) => carrierIsAllowedTo(carrier, action),
        isIboxen: carrierIsIboxen(carrier),
      },
      can: (action) => allowed(action) && carrierIsAllowedTo(carrier, action),
    }
  }, [user, carrier, carrierRolePermissions])
}
